import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const ContactPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://api.blackdogsettlement.com/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
      form.reset();
    } catch {
      // silent fail
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <head>
        <title>Contact Black Dog Settlement | Title Services Newtown Bucks County PA</title>
        <meta name="description" content="Contact Black Dog Settlement for title insurance, residential settlements, and real estate closings in Bucks, Montgomery, Chester, and Delaware County, PA. Call 215-499-0796 or email us." />
        <meta name="keywords" content="contact Black Dog Settlement, title company Newtown PA, settlement services Bucks County" />
        <link rel="canonical" href="https://www.blackdogsettlement.com/contact" />
        <meta name="geo.region" content="US-PA" />
        <meta name="geo.placename" content="Newtown, Pennsylvania" />
        <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700;800;900&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />
      </head>

      <div style={{ fontFamily: "'Inter', sans-serif" }}>
        <Navbar />

        {/* Dark hero header */}
        <section className="bg-[#0f0f0f] pt-36 pb-16">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-px bg-[#c9a84c]"></div>
              <span className="text-[#c9a84c] text-xs font-bold tracking-[0.3em] uppercase">Contact Us</span>
            </div>
            <h1
              className="text-5xl md:text-6xl font-black text-white leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Let's Talk About<br />Your <span className="text-[#c9a84c]">Settlement.</span>
            </h1>
          </div>
        </section>

        {/* 2-column: contact info + map */}
        <section className="bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col lg:flex-row gap-0">

              {/* Left — contact info */}
              <div className="w-full lg:w-[42%] py-16 lg:py-20 lg:pr-16 border-b lg:border-b-0 lg:border-r border-stone-100">

                <p className="text-stone-400 text-sm leading-relaxed mb-12">
                  Ready to close? Have questions about title insurance or the settlement process in Bucks, Montgomery, Chester, or Delaware County? Reach out — we're here to help.
                </p>

                <div className="space-y-8 mb-12">
                  <div>
                    <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-300 mb-2">Phone</p>
                    <a
                      href="tel:2154990796"
                      className="text-[#0f0f0f] font-bold text-xl hover:text-[#c9a84c] transition-colors cursor-pointer"
                    >
                      215-499-0796
                    </a>
                  </div>

                  <div>
                    <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-300 mb-2">Email</p>
                    <a
                      href="mailto:info@blackdogsettlement.com"
                      className="text-[#0f0f0f] font-bold hover:text-[#c9a84c] transition-colors cursor-pointer"
                    >
                      info@blackdogsettlement.com
                    </a>
                  </div>

                  <div>
                    <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-300 mb-2">Office</p>
                    <p className="text-[#0f0f0f] font-bold">842 Durham Rd</p>
                    <p className="text-stone-400 text-sm">Newtown, PA 18940</p>
                    <a
                      href="https://maps.google.com/?q=842+Durham+Rd+Newtown+PA+18940"
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[#c9a84c] text-xs font-bold mt-2 hover:underline cursor-pointer whitespace-nowrap"
                    >
                      Get Directions
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-external-link-line"></i>
                      </div>
                    </a>
                  </div>

                  <div>
                    <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-300 mb-2">Hours</p>
                    <p className="text-[#0f0f0f] font-bold text-sm">Mon–Fri: 9:00 AM – 5:00 PM</p>
                    <p className="text-stone-400 text-sm">Saturday: By Appointment</p>
                  </div>
                </div>

                {/* Service areas */}
                <div className="border-t border-stone-100 pt-8">
                  <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-300 mb-4">Service Areas</p>
                  <div className="space-y-2">
                    {['Bucks County, PA', 'Montgomery County, PA', 'Chester County, PA', 'Delaware County, PA'].map((c) => (
                      <div key={c} className="flex items-center gap-2 text-sm text-stone-500">
                        <div className="w-1 h-1 rounded-full bg-[#c9a84c] shrink-0"></div>
                        {c}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right — Google Map */}
              <div className="w-full lg:w-[58%] h-[420px] lg:h-auto min-h-[500px]">
                <iframe
                  title="Black Dog Settlement Office - 842 Durham Rd Newtown PA 18940"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3040.5!2d-74.934!3d40.229!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c15e5b5b5b5b5b%3A0x0!2s842+Durham+Rd%2C+Newtown%2C+PA+18940!5e0!3m2!1sen!2sus!4v1680000000001!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0, display: 'block', minHeight: '500px' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>

            </div>
          </div>
        </section>

        {/* Contact form section */}
        <section className="bg-[#f9f8f6] py-20 border-t border-stone-100">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="max-w-2xl mx-auto">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-6 h-px bg-[#c9a84c]"></div>
                <span className="text-[#c9a84c] text-xs font-bold tracking-[0.3em] uppercase">Send a Message</span>
              </div>
              <h2
                className="text-3xl md:text-4xl font-black text-[#0f0f0f] mb-10 leading-tight"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                We'll Get Back to You Promptly.
              </h2>

              {submitted ? (
                <div className="flex flex-col items-center justify-center text-center py-20 border border-stone-200 bg-white">
                  <div className="w-14 h-14 flex items-center justify-center border border-[#c9a84c] mb-5">
                    <i className="ri-check-line text-[#c9a84c] text-2xl"></i>
                  </div>
                  <h3
                    className="text-[#0f0f0f] text-2xl font-black mb-3"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    Message Received
                  </h3>
                  <p className="text-stone-400 text-sm">We'll be in touch shortly. You can also reach us at 215-499-0796.</p>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="contact-page-form"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold tracking-[0.15em] uppercase text-stone-400 mb-2">Full Name *</label>
                      <input
                        name="name"
                        type="text"
                        required
                        placeholder="Jane Smith"
                        className="w-full border border-stone-200 bg-white text-[#0f0f0f] placeholder-stone-300 px-4 py-3.5 text-sm focus:outline-none focus:border-[#c9a84c] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold tracking-[0.15em] uppercase text-stone-400 mb-2">Phone</label>
                      <input
                        name="phone"
                        type="tel"
                        placeholder="(215) 555-0000"
                        className="w-full border border-stone-200 bg-white text-[#0f0f0f] placeholder-stone-300 px-4 py-3.5 text-sm focus:outline-none focus:border-[#c9a84c] transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold tracking-[0.15em] uppercase text-stone-400 mb-2">Email Address *</label>
                    <input
                      name="email"
                      type="email"
                      required
                      placeholder="jane@example.com"
                      className="w-full border border-stone-200 bg-white text-[#0f0f0f] placeholder-stone-300 px-4 py-3.5 text-sm focus:outline-none focus:border-[#c9a84c] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold tracking-[0.15em] uppercase text-stone-400 mb-2">Service Needed</label>
                    <select
                      name="service"
                      className="w-full border border-stone-200 bg-white text-[#0f0f0f] px-4 py-3.5 text-sm focus:outline-none focus:border-[#c9a84c] transition-colors cursor-pointer"
                    >
                      <option value="">Select Service</option>
                      <option>Residential Settlement</option>
                      <option>Commercial Closing</option>
                      <option>Title Insurance</option>
                      <option>Title Search</option>
                      <option>1031 Exchange</option>
                      <option>Refinancing</option>
                      <option>Deed Preparation</option>
                      <option>Lien Search</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold tracking-[0.15em] uppercase text-stone-400 mb-2">Message</label>
                    <textarea
                      name="message"
                      rows={5}
                      maxLength={500}
                      placeholder="Tell us about your transaction..."
                      className="w-full border border-stone-200 bg-white text-[#0f0f0f] placeholder-stone-300 px-4 py-3.5 text-sm focus:outline-none focus:border-[#c9a84c] transition-colors resize-none"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#0f0f0f] hover:bg-[#c9a84c] disabled:opacity-50 text-white hover:text-[#0f0f0f] font-bold py-4 text-xs tracking-[0.2em] uppercase whitespace-nowrap cursor-pointer transition-all duration-200"
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default ContactPage;
